import { Button } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Locale } from '@synergy/models';
import { PaloConfirmationDialog, PaloDetailView } from '@synergy/components-palo';
import { PropsWithChildren, ReactElement, ReactNode, useEffect, useState } from 'react';
import { SaveIcon, TrashIcon } from '@synergy/theme-next';
import { useFormikContext } from 'formik';
import { useNavigate } from 'react-router';

import { PaloLanguageSwitch } from '../../../common/components/palo/palo-language-switch';
import { changeSelectedLanguage, resetSelectedLanguage } from '../../../common/actions/selected-language-actions';
import { useAppSelector } from '../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

export interface PaloContentDetailShellProps {
    /** Translation id of the screen title. */
    title: string;
    isNew: boolean;
    onDelete: () => void;
    /** Where to return to once the entry is deleted. */
    listRoute: string;
    deleteTitle: ReactNode;
    deleteWarning: ReactNode;
    /** Whether the entry is translated; hides the language switch when it is not. */
    translatable?: boolean;
}

/**
 * PALO chrome for the content management detail screens. They all carry the
 * same header — crumb, save, delete, language switch — and the same two
 * confirmations, so the shape lives here and each screen contributes only its
 * form. Must be rendered inside a Formik context.
 */
export const PaloContentDetailShell = ({
    title,
    isNew,
    onDelete,
    listRoute,
    deleteTitle,
    deleteWarning,
    translatable = true,
    children,
}: PropsWithChildren<PaloContentDetailShellProps>): ReactElement => {
    const navigate = useNavigate();
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const formik = useFormikContext<unknown>();
    const supportedLocales = useAppSelector(state => state.supportedLocales);

    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [pendingLocale, setPendingLocale] = useState<Locale | null>(null);

    useEffect(() => {
        dispatch(resetSelectedLanguage());
    }, [dispatch]);

    // Otherwise the browser tab keeps the name of the list this came from
    // (#2212, R-22).
    useEffect(() => {
        document.title = intl.formatMessage({ id: title });
    }, [intl, title]);

    // Switching the language reloads the form from the store, so unsaved input
    // would be lost without asking first.
    const handleBeforeSwitch = (locale: Locale) => {
        if (!formik.dirty) {
            return true;
        }
        setPendingLocale(locale);
        return false;
    };

    const handleDiscardAndSwitch = () => {
        formik.resetForm();
        if (pendingLocale) {
            dispatch(changeSelectedLanguage(pendingLocale));
        }
        setPendingLocale(null);
    };

    const handleDelete = () => {
        setShowDeleteWarning(false);
        navigate(listRoute);
        onDelete();
    };

    // The title of these screens is the kind of entry, not the name of one,
    // so the crumb names the area and leads back to the list it came from.
    const breadcrumbs = [{ label: 'Inhalte', onClick: () => navigate(listRoute) }];

    return (
        <PaloDetailView
            breadcrumbs={breadcrumbs}
            title={<FormattedMessage id={title} />}
            dirty={formik.dirty}
            actions={
                <>
                    {translatable && supportedLocales.length > 1 && (
                        <PaloLanguageSwitch onBeforeSwitch={handleBeforeSwitch} />
                    )}
                    <Button
                        size="small"
                        color="error"
                        startIcon={<TrashIcon />}
                        disabled={isNew}
                        onClick={() => setShowDeleteWarning(true)}
                    >
                        <FormattedMessage id="app.common.delete" />
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={() => formik.handleSubmit()}
                    >
                        <FormattedMessage id="app.common.save" />
                    </Button>
                </>
            }
        >
            {children}

            <PaloConfirmationDialog
                isVisible={showDeleteWarning}
                onCancel={() => setShowDeleteWarning(false)}
                onConfirm={handleDelete}
                title={deleteTitle}
                action={<FormattedMessage id="app.common.delete" />}
            >
                {deleteWarning}
            </PaloConfirmationDialog>

            <PaloConfirmationDialog
                isVisible={!!pendingLocale}
                onCancel={() => setPendingLocale(null)}
                onConfirm={handleDiscardAndSwitch}
                title={<FormattedMessage id="app.contentManagement.language.switch" />}
                action={<FormattedMessage id="app.common.deleteChanges" />}
            >
                <FormattedMessage id="app.contentManagement.language.switchWarning" />
            </PaloConfirmationDialog>
        </PaloDetailView>
    );
};
